/**
 * WaOrderCard — render de un orderMessage (pedido desde catálogo WhatsApp Business).
 *
 * Layout estilo WhatsApp Web/iOS nativo:
 *   ┌─ Pedido enviado por catálogo ───────────┐
 *   ├─────────────────────────────────────────┤
 *   │ ┌────┐                                  │
 *   │ │img │  🛒 N artículos                  │
 *   │ │70px│  S/ XX.XX (total estimado)       │
 *   │ └────┘                                  │
 *   ├─────────────────────────────────────────┤
 *   │            Ver solicitud de pedido                 │
 *   └─────────────────────────────────────────┘
 *
 * Hero image: si Feature 5c-B ya pre-fetcheo los items (order_info.items
 * con imageUrl), usa la foto del primer producto. Fallback al thumbnail
 * comprimido que vino en el orderMessage.
 */

import { Text, Tooltip } from '@shopify/polaris';

interface OrderItem {
  id?: string;
  name?: string;
  imageUrl?: string;
  price?: number | string;
  currency?: string;
  quantity?: number;
}

interface OrderInfo {
  orderId?: string;
  token?: string;
  totalAmount1000?: number;
  currency?: string;
  itemCount?: number;
  message?: string;
  orderTitle?: string;
  sellerJid?: string;
  status?: number;
  items?: OrderItem[];
}

interface Props {
  order: OrderInfo;
  thumbnail: string | null;
  onViewDetails?: () => void;
}

function formatTotal(amount1000?: number, currency?: string): string {
  if (typeof amount1000 !== 'number' || amount1000 <= 0) return '';
  const value = amount1000 / 1000;
  const cur = currency ?? '';
  return `${cur} ${value.toFixed(2)}`.trim();
}

export function WaOrderCard({ order, thumbnail, onViewDetails }: Props) {
  const total = formatTotal(order.totalAmount1000, order.currency);
  const itemCount = order.itemCount ?? 0;
  const customerMsg = order.message?.trim();
  // Hero: priorizar imagen full del primer item (post-pre-fetch),
  // fallback al thumbnail comprimido del orderMessage.
  const firstItemImage = order.items?.[0]?.imageUrl;
  const heroImage = firstItemImage ?? thumbnail ?? null;
  const itemCountLabel = itemCount > 0
    ? `🛒 ${itemCount} artículo${itemCount === 1 ? '' : 's'}`
    : '🛒 Pedido';

  const detailDisabled = !onViewDetails;
  const detailButton = (
    <button
      type="button"
      disabled={detailDisabled}
      onClick={onViewDetails}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: 'transparent',
        border: 'none',
        borderTop: '1px solid var(--p-color-border-subdued)',
        cursor: detailDisabled ? 'not-allowed' : 'pointer',
        color: detailDisabled ? 'var(--p-color-text-disabled)' : 'var(--p-color-text-link)',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
      }}
    >
      Ver solicitud de pedido
    </button>
  );

  return (
    <div
      style={{
        width: 290,
        background: 'var(--p-color-bg-surface-secondary)',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--p-color-border-subdued)',
      }}
    >
      {/* Header con icono + título */}
      <div style={{
        padding: '8px 12px',
        background: 'var(--p-color-bg-fill-info-secondary)',
        borderBottom: '1px solid var(--p-color-border-subdued)',
      }}>
        <Text as="p" variant="bodySm" fontWeight="semibold">Pedido enviado por catálogo</Text>
      </div>

      {/* Body: thumbnail pequeño + info al lado (layout horizontal nativo WhatsApp) */}
      <div style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
        {heroImage ? (
          <img
            src={heroImage}
            alt="Producto"
            style={{
              width: 70,
              height: 70,
              objectFit: 'cover',
              borderRadius: 6,
              flexShrink: 0,
            }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: 70,
            height: 70,
            borderRadius: 6,
            background: 'var(--p-color-bg-fill-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 24,
          }}>
            📦
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Text as="p" variant="bodyMd" fontWeight="semibold">{itemCountLabel}</Text>
          {total ? (
            <Text as="p" variant="bodySm" tone="subdued">{total} (total estimado)</Text>
          ) : null}
        </div>
      </div>

      {/* Nota del cliente (si la escribió junto con el pedido) */}
      {customerMsg ? (
        <div style={{
          margin: '0 12px 8px',
          padding: '6px 10px',
          background: 'var(--p-color-bg-surface-tertiary)',
          borderLeft: '3px solid var(--p-color-border-info)',
          borderRadius: 4,
          fontSize: 12,
        }}>
          <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.7 }}>NOTA DEL CLIENTE</span>
          <div style={{ marginTop: 2 }}>{customerMsg}</div>
        </div>
      ) : null}

      {/* Footer: "Ver detalles" como link separado, full-width */}
      {onViewDetails ? detailButton : (
        <Tooltip content="Disponible cuando el fork exponga getOrderDetails">
          {detailButton}
        </Tooltip>
      )}
    </div>
  );
}
